import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../App';
import Navigation from './Navigation';
import Footer from './Footer';
import PhotoViewer from './PhotoViewer';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  RotateCcw, 
  Plus, 
  Clock, 
  AlertTriangle,
  Camera,
  X,
  Image,
  Trash2,
  Cookie,
  Coffee,
  Cake
} from 'lucide-react';

const ReturnsEntry = () => {
  const { user, API } = useContext(UserContext);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedCounter, setSelectedCounter] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [recentEntries, setRecentEntries] = useState([]);

  const counters = [
    { id: 'pain', name: 'Pains', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'viennoiserie', name: 'Viennoiserie', color: 'bg-purple-100 text-purple-800' },
    { id: 'gateaux', name: 'Gâteaux', color: 'bg-pink-100 text-pink-800' }
  ];

  const returnReasons = [
    'Cassé',
    'Déformé', 
    'Gâté',
    'Brûlé',
    'Mal cuit',
    'Défaut de fabrication',
    'Périmé',
    'Autre'
  ];

  useEffect(() => {
    let mounted = true;
    
    const loadData = async () => {
      try {
        setPageLoading(true);
        
        const [productsRes, entriesRes] = await Promise.all([
          axios.get(`${API}/products`).catch(() => ({ data: [] })),
          axios.get(`${API}/returns?date=${new Date().toISOString().split('T')[0]}`).catch(() => ({ data: [] }))
        ]);
        
        if (mounted) {
          setAllProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
          setRecentEntries(Array.isArray(entriesRes.data) ? entriesRes.data.slice(0, 10) : []);
        }
      } catch (error) {
        console.error('Error loading returns data:', error);
        if (mounted) {
          toast.error('Erreur lors du chargement');
          setAllProducts([]);
          setRecentEntries([]);
        }
      } finally {
        if (mounted) {
          setPageLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      mounted = false;
    };
  }, [API]);

  const getFilteredProducts = () => {
    if (!selectedCounter || !Array.isArray(allProducts)) return [];
    return allProducts.filter(product => product && product.counter === selectedCounter);
  };

  const getProductDetails = (productId) => {
    if (!productId || !Array.isArray(allProducts)) return null;
    return allProducts.find(p => p && p.id === productId) || null;
  };

  const handleCounterSelect = (counterId) => {
    try {
      console.log('Selecting returns counter:', counterId);
      setSelectedCounter(counterId);
      setSelectedProduct('');
      setQuantity('');
      setReason('');
    } catch (error) {
      console.error('Error selecting counter:', error);
      toast.error('Erreur lors de la sélection du guichet');
    }
  };

  const handleProductChange = (event) => {
    try {
      const productId = event.target.value;
      console.log('Returns product selection:', productId);
      
      if (!productId) {
        setSelectedProduct('');
        setQuantity('');
        return;
      }
      
      const product = getProductDetails(productId);
      console.log('Found returns product:', product);
      
      if (product) {
        setSelectedProduct(productId);
        console.log('Returns product selection successful:', product.name);
      } else {
        console.error('Returns product not found:', productId);
        setSelectedProduct('');
        toast.error('Produit introuvable');
      }
    } catch (error) {
      console.error('Critical error in returns product selection:', error);
      setSelectedProduct('');
      setQuantity('');
      setReason('');
      toast.error('Erreur lors de la sélection du produit');
    }
  };

  const handleReasonChange = (event) => {
    try {
      setReason(event.target.value);
    } catch (error) {
      console.error('Error setting reason:', error);
    }
  };

  const handlePhotoCapture = (event) => {
    try {
      const files = Array.from(event.target.files || []);
      
      files.forEach(file => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const base64 = e.target.result;
              setPhotos(prev => [...prev, {
                id: Date.now() + Math.random(),
                data: base64,
                name: file.name
              }]);
            } catch (error) {
              console.error('Error processing photo:', error);
            }
          };
          reader.readAsDataURL(file);
        }
      });
      
      event.target.value = '';
    } catch (error) {
      console.error('Error capturing photo:', error);
      toast.error('Erreur lors de la capture de photo');
    }
  };

  const removePhoto = (photoId) => {
    try {
      setPhotos(prev => prev.filter(photo => photo.id !== photoId));
    } catch (error) {
      console.error('Error removing photo:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (!selectedProduct || !quantity || parseFloat(quantity) <= 0 || !reason.trim()) {
        toast.error('Veuillez remplir tous les champs correctement');
        return;
      }

      setLoading(true);

      const photoData = photos.map(photo => photo.data);
      
      await axios.post(`${API}/returns`, {
        product_id: selectedProduct,
        quantity_returned: parseFloat(quantity),
        reason: reason.trim(),
        photos: photoData,
        user_id: user.id
      });

      toast.success('Retour enregistré avec succès !');
      
      // Reset form
      setSelectedProduct('');
      setQuantity('');
      setReason('');
      setPhotos([]);
      
      // Refresh entries
      const entriesRes = await axios.get(`${API}/returns?date=${new Date().toISOString().split('T')[0]}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 10));
      }
    } catch (error) {
      console.error('Error creating return record:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      if (!entryId) return;
      
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce retour ?')) {
        return;
      }

      await axios.delete(`${API}/returns/${entryId}?user_role=${user.role}`);
      toast.success('Retour supprimé !');
      
      const entriesRes = await axios.get(`${API}/returns?date=${new Date().toISOString().split('T')[0]}`);
      if (Array.isArray(entriesRes.data)) {
        setRecentEntries(entriesRes.data.slice(0, 10));
      }
    } catch (error) {
      console.error('Error deleting return record:', error);
      if (error.response?.status === 403) {
        toast.error('Accès refusé');
      } else {
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  const canDelete = () => {
    return user && (user.role === 'owner' || user.role === 'admin');
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'Heure inconnue';
    try {
      return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Heure invalide';
    }
  };

  const calculateLoss = () => {
    try {
      if (selectedProduct && quantity) {
        const product = getProductDetails(selectedProduct);
        return product ? product.price * parseInt(quantity) : 0;
      }
      return 0;
    } catch (error) {
      return 0;
    }
  };

  const getCounterIcon = (counterId) => {
    switch (counterId) {
      case 'pain':
        return <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-4 w-4 object-cover rounded" />;
      case 'viennoiserie':
        return <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-4 w-4 object-cover rounded" />;
      case 'gateaux':
        return <Cake className="h-4 w-4" />;
      default:
        return <RotateCcw className="h-4 w-4" />;
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des retours...</p>
          </div>
        </div>
      </div>
    );
  }

  const filteredProducts = getFilteredProducts();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50">
      <Navigation />
      
      <div className="content-wrapper px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-width-container flex items-center justify-between mb-8 fade-in">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Saisie des Retours
            </h1>
            <p className="text-gray-600">
              Enregistrez les produits non vendables (cassés, gâtés, etc.)
            </p>
          </div>
        </div>

        <div className="max-width-cards grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Returns Form */}
          <Card className="bg-white/70 glass border-0 shadow-lg slide-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-red-600" />
                Nouveau Retour
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Counter Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    Guichet
                  </Label>
                  <div className="grid grid-cols-3 gap-3">
                    {counters.map((counter) => (
                      <button
                        key={counter.id}
                        type="button"
                        onClick={() => handleCounterSelect(counter.id)}
                        className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                          selectedCounter === counter.id
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-red-300'
                        }`}
                        data-testid={`counter-${counter.id}`}
                      >
                        {counter.id === 'pain' && (
                          <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/32b5mga4_image.png" alt="Pains" className="h-8 w-8 mx-auto mb-2 object-cover rounded" />
                        )}
                        {counter.id === 'viennoiserie' && (
                          <img src="https://customer-assets.emergentagent.com/job_baker-point-of-sale/artifacts/d7eif7ef_image.png" alt="Viennoiseries" className="h-8 w-8 mx-auto mb-2 object-cover rounded" />
                        )}
                        {counter.id === 'gateaux' && (
                          <Cake className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                        )}
                        <p className="text-sm font-medium">{counter.name}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Product Selection - SAME PRODUCTS AS PRODUCTION */}
                {selectedCounter && filteredProducts.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Produit ({filteredProducts.length} disponible(s))
                    </Label>
                    
                    <select
                      value={selectedProduct}
                      onChange={handleProductChange}
                      className="w-full h-12 px-4 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white hover:border-red-400 transition-colors"
                      data-testid="product-select-safe"
                    >
                      <option value="" disabled>Sélectionnez un produit</option>
                      {filteredProducts.map((product) => (
                        <option key={`returns-product-${product.id}`} value={product.id}>
                          {product.name} - {product.price} F CFA
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quantity Input */}
                {selectedProduct && (
                  <div className="space-y-2">
                    <Label htmlFor="quantity" className="text-sm font-medium text-gray-700">
                      Quantité retournée
                    </Label>
                    <Input
                      id="quantity"
                      type="number"
                      step="0.1"
                      min="0.1"
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="h-12 text-base"
                      placeholder="Ex: 3 ou 2.5"
                      data-testid="quantity-input"
                    />
                  </div>
                )}

                {/* Reason Selection - HTML SELECT */}
                {selectedProduct && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-gray-700">
                      Motif du retour
                    </Label>
                    <select
                      value={reason}
                      onChange={handleReasonChange}
                      className="w-full h-12 px-4 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                      data-testid="reason-select-safe"
                    >
                      <option value="" disabled>Sélectionnez un motif</option>
                      {returnReasons.map((reasonOption) => (
                        <option key={reasonOption} value={reasonOption}>
                          {reasonOption}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Custom Reason Input */}
                {reason === 'Autre' && (
                  <div className="space-y-2">
                    <Label htmlFor="customReason" className="text-sm font-medium text-gray-700">
                      Précisez le motif
                    </Label>
                    <Textarea
                      id="customReason"
                      value=""
                      onChange={(e) => setReason(e.target.value)}
                      className="min-h-[80px] text-base"
                      placeholder="Décrivez le motif du retour..."
                      data-testid="custom-reason-input"
                    />
                  </div>
                )}

                {/* Photo Capture */}
                {selectedProduct && (
                  <div className="space-y-2">
                    <Label htmlFor="photos" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Camera className="h-4 w-4" />
                      Photos des produits retournés (optionnel)
                    </Label>
                    <input
                      id="photos"
                      type="file"
                      accept="image/*"
                      multiple
                      capture="environment"
                      onChange={handlePhotoCapture}
                      className="hidden"
                    />
                    
                    <Button
                      type="button"
                      onClick={() => document.getElementById('photos')?.click()}
                      variant="outline"
                      className="w-full h-12 border-2 border-dashed border-gray-300 hover:border-blue-400"
                      data-testid="photo-capture-btn"
                    >
                      <Camera className="h-5 w-5 mr-2" />
                      Prendre une photo
                    </Button>
                    
                    {/* Photo Preview */}
                    {photos.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {photos.map((photo) => (
                          <div key={photo.id} className="relative group">
                            <img
                              src={photo.data}
                              alt="Produit retourné"
                              className="w-full h-32 object-cover rounded-lg border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => removePhoto(photo.id)}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Loss Preview */}
                {selectedProduct && quantity && (
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-red-800">
                        Perte estimée
                      </span>
                      <span className="text-lg font-bold text-red-600">
                        {calculateLoss().toLocaleString()} F CFA
                      </span>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={loading || !selectedProduct || !quantity || !reason.trim()}
                  className="w-full h-12 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-medium rounded-lg shadow-lg"
                  data-testid="submit-return-btn"
                >
                  {loading ? (
                    <>
                      <div className="spinner mr-2"></div>
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Enregistrer le Retour
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Recent Returns */}
          <Card className="bg-white/70 glass border-0 shadow-lg slide-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-red-600" />
                Retours Récents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Array.isArray(recentEntries) && recentEntries.length > 0 ? (
                <div className="space-y-3">
                  {recentEntries.map((entry, index) => {
                    if (!entry || !entry.id) return null;
                    
                    const product = getProductDetails(entry.product_id);
                    const counter = counters.find(c => c.id === product?.counter);
                    const loss = product ? product.price * (entry.quantity_returned || 0) : 0;
                    
                    return (
                      <div key={`returns-entry-${entry.id}-${index}`} className="p-3 bg-gray-50 rounded-lg border-l-4 border-red-500">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <div className={`p-2 rounded-full ${counter?.color || 'bg-gray-100'}`}>
                              {getCounterIcon(counter?.id)}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {product?.name || 'Produit inconnu'}
                              </p>
                              <p className="text-sm text-gray-600">
                                {formatTime(entry.time)} • {entry.reason || 'Aucun motif'}
                              </p>
                              
                              <PhotoViewer 
                                photos={entry.photos || []} 
                                productName={product?.name || 'Produit retourné'} 
                              />
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge variant="destructive" className="mb-1">
                              {entry.quantity_returned || 0} retournés
                            </Badge>
                            <p className="text-sm font-semibold text-red-600">
                              -{product ? 
                                `${(parseFloat(entry.quantity_returned || 0) * product.price).toLocaleString('fr-FR')} F CFA`
                                : '0 F CFA'
                              }
                            </p>
                            {canDelete() && (
                              <button
                                onClick={() => handleDelete(entry.id)}
                                className="p-1 hover:bg-red-100 rounded transition-colors mt-1 block"
                                title="Supprimer"
                                data-testid={`delete-return-${entry.id}`}
                              >
                                <Trash2 className="h-3 w-3 text-gray-400 hover:text-red-600" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }).filter(Boolean)}
                </div>
              ) : (
                <div className="text-center py-8">
                  <RotateCcw className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucun retour enregistré aujourd'hui</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Reasons */}
        <Card className="max-width-cards mt-8 bg-white/70 glass border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Motifs Fréquents</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {returnReasons.slice(0, -1).map((reasonOption) => (
                <button
                  key={reasonOption}
                  onClick={() => setReason(reasonOption)}
                  className={`px-4 py-2 rounded-full border-2 transition-all duration-200 text-sm font-medium ${
                    reason === reasonOption
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-gray-300 hover:border-red-400 text-gray-700'
                  }`}
                >
                  {reasonOption}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default ReturnsEntry;